import { TextFieldModule } from '@angular/cdk/text-field'
import { CommonModule } from '@angular/common'
import {
  Component,
  DestroyRef,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  computed,
  effect,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'

// no browser sanitizer needed (mirror removed)
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { clearNode, createDomain } from 'effector'
import { last, slice } from 'lodash/fp'
import { MenuModule } from 'primeng/menu'
import { TooltipModule } from 'primeng/tooltip'

import { debounceTime, distinctUntilChanged } from 'rxjs/operators'

import { FlxIconModule } from '@flowx/angular-ui-toolkit'
import {
  AiAgent,
  FileExtension,
  FileMimeTypeLabel,
  ShortcutIconName,
} from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { resetInputFileValueFromEvent } from '@flowx/designer/modules/new-ai/common/ai.utils'
import {
  AiShortcutComponent,
  IconType,
  ShortcutConfig,
} from '@flowx/designer/modules/new-ai/modules/command-palette/components/shortcut/shortcut.component'
// eslint-disable-next-line import/no-cycle
import { AiActions } from '@flowx/designer/modules/new-ai/store/ai.actions'
import { AiSelectors } from '@flowx/designer/modules/new-ai/store/ai.selectors'
import { FlxAutofocusDirective } from '@flowx/designer/shared/directives/autofocus.directive'

@Component({
  selector: 'flx-ai-side-type-area',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FlxIconModule,
    TextFieldModule,
    AiShortcutComponent,
    TooltipModule,
    MenuModule,
    FlxAutofocusDirective,
  ],
  templateUrl: './side-type-area.component.html',
  styleUrls: ['./side-type-area.component.scss'],
})
export class AiSideTypeAreaComponent implements OnDestroy, OnInit, OnChanges {
  #effector = createDomain('AiTypeAreaComponent')
  #store = inject(Store)
  #actions$ = inject(Actions)
  #destroyRef = inject(DestroyRef)

  #imageFiles = this.#store.selectSignal(AiSelectors.uploadedImageFiles)
  #bpmnFiles = this.#store.selectSignal(AiSelectors.uploadedBpmnFiles)

  #bpmnFilesSelected = computed(() => !!this.#bpmnFiles().length)

  private typingTimeout: any
  private typingInterval: any
  private hintIndex = 0
  hint = input<string>('')
  hints = input<string[] | null>(null)
  currentValue = output<string>()
  value = input<string>('')
  submitted = output<boolean>()
  bigger = input<boolean>(false)
  showMentionButton = input<boolean>(false)
  dark = input<boolean>(true)
  textarea = viewChild<ElementRef>('textarea')
  mentionMenu = viewChild<any>('mentionMenu')
  menuCommand = viewChild<any>('menuCommand')
  imageUpload = viewChild<ElementRef>('imageUpload')
  docUpload = viewChild<ElementRef>('docUpload')
  processUpload = viewChild<ElementRef>('processUpload')

  typeControl = new FormControl(this.value(), [Validators.required, Validators.maxLength(10000)])

  submitShortcut: ShortcutConfig['icons'] = [
    { type: IconType.ICON, name: ShortcutIconName.ArrowUp },
  ]

  showUpload = this.#store.selectSignal(AiSelectors.isUploadAllowed)
  isUploadDisabled = this.#store.selectSignal(AiSelectors.isUploadDisabled)
  allowedFileTypes = this.#store.selectSignal(AiSelectors.allowedFileTypes)
  allowedDocumentFileTypes = this.#store.selectSignal(AiSelectors.allowedDocumentFileTypes)
  allowedImageFileTypes = this.#store.selectSignal(AiSelectors.allowedImageFileTypes)
  allowedProcessFileTypes = this.#store.selectSignal(AiSelectors.allowedProcessFileTypes)
  isMultipleDocumentUploadAllowed = this.#store.selectSignal(
    AiSelectors.isMultipleDocumentUploadAllowed
  )
  isMultipleProcessUploadAllowed = this.#store.selectSignal(
    AiSelectors.isMultipleProcessUploadAllowed
  )
  isMultipleImageUploadAllowed = this.#store.selectSignal(AiSelectors.isMultipleImageUploadAllowed)
  currentAgent = this.#store.selectSignal(AiSelectors.selectedAgent)
  contextAgent = this.#store.selectSignal(AiSelectors.contextAgent)

  items = this.getMenuItems()
  // Agent / @key context menu state (only one agent can be selected)
  showMentionMenu = false
  agentOptions: string[] = [
    'Assistant',
    'Analyst',
    'Architect',
    'Designer',
    'Developer',
    'Integrator',
  ]
  filteredAgentOptions: string[] = [...this.agentOptions]
  agentQuery = ''
  menuPosition: { x: number; y: number } = { x: 0, y: 0 }

  // selected agent (only one) stored in a control
  agentControl = new FormControl<string | null>(null)
  // keyboard navigation index for filtered options
  highlightedIndex = 0
  // whether menu popup is opened
  menuOpened = false

  isMultipleUploadAllowed = this.#store.selectSignal(AiSelectors.isMultipleUploadAllowed)

  // ─── Audit button (Slide 19 demo) ────────────────────────────────────
  // Shows a one-click "Audit this …" button when the current page is a
  // Process Designer or UI Designer (incl. UI Flow / Reusable UI Template).
  // The button pre-fills the audit prompt + sets the recommended_intent
  // to "route_to_agent: auditor" via setSelectedAgent(AUDITOR). The actual
  // submit still goes through the coordinator (typeAreaSubmit -> getIntent),
  // so the coordinator-routing story on slide 19 holds.
  auditConfig = computed<{ label: string; prompt: string } | null>(() => {
    const ctx = this.contextAgent()
    if (ctx === AiAgent.DESIGNER) {
      return {
        label: 'Audit this screen',
        prompt:
          'Audit this user interface for accessibility, consent, PII handling, and input-type issues. Cite the relevant articles and suggest fixes per finding.',
      }
    }
    if (ctx === AiAgent.ANALYST) {
      return {
        label: 'Audit this process',
        prompt:
          'Audit this business process for compliance, human-oversight gaps, and risk. Cite the relevant articles and suggest a fix per finding.',
      }
    }
    return null
  })

  uploadFileTooltip = computed(() => {
    const allowedFileTypes = this.allowedFileTypes().filter((type) => type !== FileExtension.BPMN)

    if (this.isUploadDisabled()) {
      return ''
    }

    const allowedFileTypesStr =
      allowedFileTypes.length === 1
        ? FileMimeTypeLabel[allowedFileTypes[0]]
        : `${slice(0, -2, allowedFileTypes)
            .map((type) => FileMimeTypeLabel[type])
            .join(', ')} and ${FileMimeTypeLabel[last(allowedFileTypes)]}`

    return `Upload ${allowedFileTypesStr} files to use as context for your prompt.`
  })

  get promptInvalidLengthMsg(): string {
    const { actualLength, requiredLength } = this.typeControl.errors?.maxlength || {}

    return this.typeControl.errors?.maxlength
      ? `Character Limit Exceeded ${actualLength} / ${requiredLength}`
      : null
  }

  animatedHint = ''

  constructor() {
    this.#onCopyToChatInputField()

    effect(() => {
      const bpmnFilesSelected = this.#bpmnFilesSelected()
      if (bpmnFilesSelected) {
        this.typeControl.disable()
      } else {
        this.typeControl.enable()
      }
    })
  }

  ngOnInit(): void {
    this.startTypingInterval()
    this.typeControl?.valueChanges
      .pipe(
        debounceTime(300), // Wait 300ms after typing stops
        distinctUntilChanged(), // Only emit if value is different from last emitted value
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((value) => {
        this.currentValue.emit(value)
      })
  }

  public focusTextarea(): void {
    this.textarea()?.nativeElement.focus()
  }

  onTextareaInput(event: Event): void {
    // existing emission
    this.onTypeAreaValue(event)

    const textarea = event.target as HTMLTextAreaElement
    const value = textarea.value
    const caret = textarea.selectionStart

    // find last @ before caret
    const atIndex = value.lastIndexOf('@', caret - 1)
    if (atIndex === -1) {
      this.closeMentionMenu()
      return
    }

    // ensure @ is a mention (start of string or preceded by whitespace)
    const charBefore = atIndex > 0 ? value[atIndex - 1] : ' '
    if (!/\s/.test(charBefore)) {
      this.closeMentionMenu()
      return
    }

    const token = value.slice(atIndex + 1, caret)
    // if token contains whitespace or newline, abort
    if (/\s/.test(token)) {
      this.closeMentionMenu()
      return
    }

    this.agentQuery = token
    this.filteredAgentOptions = this.agentOptions.filter((opt) =>
      opt.toLowerCase().includes(this.agentQuery.toLowerCase())
    )
    this.highlightedIndex = 0

    // position menu near textarea bottom-left as a simple fallback
    const rect = textarea.getBoundingClientRect()
    this.menuPosition = { x: rect.left + 8, y: rect.top + rect.height - 6 }
    // open or refresh popup menu anchored to textarea
    const ev2 = { currentTarget: textarea, target: textarea } as unknown as Event
    if (!this.menuOpened) {
      this.mentionMenu()?.toggle(ev2)
      this.menuOpened = true
      // restore focus/caret to textarea after menu opens
      setTimeout(() => {
        try {
          textarea.focus()
          textarea.setSelectionRange(caret, caret)
        } catch (e) {
          /* ignore */
        }
      }, 500)
    } else {
      // refresh: hide then show so menu picks up the new model
      try {
        this.mentionMenu()?.hide?.()
      } catch (e) {
        /* ignore */
      }
      // reopen on next tick so menu re-reads the model
      setTimeout(() => {
        this.mentionMenu()?.toggle(ev2)
        // restore focus/caret after refresh
        try {
          textarea.focus()
          textarea.setSelectionRange(caret, caret)
        } catch (e) {
          /* ignore */
        }
      }, 500)
    }
    // if an agent is already selected, remove any agent tokens from the textarea content
    if (this.agentControl.value) {
      const escape = (s: string): string => s.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')
      const agentsPattern = this.agentOptions.map((a) => escape(a)).join('|')
      const removeAgentRegex = new RegExp(`@(?:${agentsPattern})\\s?`, 'g')
      const cleaned = value.replace(removeAgentRegex, '')
      if (cleaned !== value) {
        this.typeControl.setValue(cleaned)
        return
      }
    }
  }

  onTextareaKeydown(event: KeyboardEvent): void {
    const { key, shiftKey, metaKey } = event
    const submitKeyEv = key.toLowerCase() === 'enter' && !shiftKey && !metaKey
    const newLineKeyEv = key.toLowerCase() === 'enter' && metaKey

    if (newLineKeyEv) {
      this.typeControl.setValue(this.typeControl.value + '\n')
    }

    if (submitKeyEv) {
      // if menu is open, select highlighted agent; otherwise submit
      if (this.menuOpened) {
        event.preventDefault()
        event.stopPropagation()
        const sel = this.filteredAgentOptions[this.highlightedIndex] || this.filteredAgentOptions[0]
        if (sel) {
          this.selectMention(sel)
        }
      } else {
        event.preventDefault()
        event.stopPropagation()
        this.submitTypeControl()
      }
    }

    if (key === 'Escape' && this.menuOpened) {
      event.preventDefault()
      this.closeMentionMenu()
    }
    if (key === 'ArrowDown' && this.menuOpened) {
      event.preventDefault()
      this.highlightedIndex = Math.min(
        this.filteredAgentOptions.length - 1,
        this.highlightedIndex + 1
      )
      return
    }
    if (key === 'ArrowUp' && this.menuOpened) {
      event.preventDefault()
      this.highlightedIndex = Math.max(0, this.highlightedIndex - 1)
      return
    }
    // Enter handled in filterKeyEvents; do not handle here to avoid duplicate actions
  }

  selectMention(option: string): void {
    const textareaEl = this.textarea()?.nativeElement as HTMLTextAreaElement
    if (!textareaEl) {
      return
    }

    const value = textareaEl.value
    const caret = textareaEl.selectionStart
    const atIndex = value.lastIndexOf('@', caret - 1)
    // If there's no @ token (menu opened via button), accept selection directly
    if (atIndex === -1) {
      // set control value (single agent) and persist to store
      this.agentControl.setValue(option)
      try {
        const agentEnum = AiAgent[option.toUpperCase() as keyof typeof AiAgent]
        this.#store.dispatch(AiActions.setSelectedAgent({ selectedAgent: agentEnum }))
      } catch (e) {
        // ignore mapping errors
      }

      this.closeMentionMenu()
      return
    }

    // replace any existing agent mentions in the textarea and set selected agent
    const valueBefore = value.slice(0, atIndex)
    const valueAfter = value.slice(caret)

    const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')
    const agentsPattern = this.agentOptions.map((a) => escapeRegExp(a)).join('|')
    const removeAgentRegex = new RegExp(`@(?:${agentsPattern})\\s?`, 'g')

    const before = valueBefore.replace(removeAgentRegex, '')
    const after = valueAfter.replace(removeAgentRegex, '')

    // remove any agent tokens from the textarea (do NOT insert the token)
    const newValue = (before + after).trimStart()
    this.typeControl.setValue(newValue)

    // move caret to where the insertion point was (after 'before')
    const newCaret = before.length
    setTimeout(() => {
      textareaEl.focus()
      const pos = Math.min(newCaret, this.typeControl.value?.length || 0)
      textareaEl.setSelectionRange(pos, pos)
    }, 0)

    // set control value (single agent) and persist to store
    this.agentControl.setValue(option)
    // persist to store so other parts can react
    try {
      const agentEnum = AiAgent[option.toUpperCase() as keyof typeof AiAgent]
      this.#store.dispatch(AiActions.setSelectedAgent({ selectedAgent: agentEnum }))
    } catch (e) {
      // ignore mapping errors
    }

    this.closeMentionMenu()
  }

  closeMentionMenu(): void {
    this.showMentionMenu = false
    this.agentQuery = ''
    this.filteredAgentOptions = [...this.agentOptions]
    this.menuOpened = false
    try {
      this.mentionMenu()?.hide?.()
    } catch (e) {
      /* ignore */
    }
    // restore focus to textarea after menu closes
    setTimeout(() => {
      try {
        const ta = this.textarea()?.nativeElement as HTMLTextAreaElement
        if (ta) {
          ta.focus()
          const pos = ta.selectionStart ?? (ta.value?.length || 0)
          ta.setSelectionRange(pos, pos)
        }
      } catch (e) {
        /* ignore */
      }
    }, 0)
  }

  toggleAgentMenu(ev: Event): void {
    this.mentionMenu()?.toggle(ev)
    this.menuOpened = !this.menuOpened
    // if we just closed the menu, restore focus to textarea
    if (!this.menuOpened) {
      setTimeout(() => {
        try {
          const ta = this.textarea()?.nativeElement as HTMLTextAreaElement
          if (ta) {
            ta.focus()
            const pos = ta.selectionStart ?? (ta.value?.length || 0)
            ta.setSelectionRange(pos, pos)
          }
        } catch (e) {
          /* ignore */
        }
      }, 0)
    }
  }

  removeMention(): void {
    // clear selected agent and remove it from textarea
    this.agentControl.setValue(null)
    // persist removal to store
    this.#store.dispatch(AiActions.setSelectedAgent({ selectedAgent: null }))
    const currentText = this.typeControl.value || ''
    const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')
    const agentsPattern = this.agentOptions.map((a) => escapeRegExp(a)).join('|')
    const removeAgentRegex = new RegExp(`@(?:${agentsPattern})\\s?`, 'g')
    this.typeControl.setValue(currentText.replace(removeAgentRegex, ''))
  }

  onTypeAreaValue(event: Event): void {
    const textareaValue = (event.target as HTMLTextAreaElement).value
    this.currentValue.emit(textareaValue)
  }

  // One-click audit: pre-fill the prompt and pin Auditor as the recommended
  // route. We don't auto-submit — the user reviews the prompt and hits Enter.
  // The submit goes through typeAreaSubmit -> getIntent (coordinator).
  triggerAudit(): void {
    const cfg = this.auditConfig()
    if (!cfg) {
      return
    }
    this.typeControl.setValue(cfg.prompt)
    this.agentControl.setValue('Auditor')
    this.#store.dispatch(AiActions.setSelectedAgent({ selectedAgent: AiAgent.AUDITOR }))
    this.focusTextarea()
  }

  getMenuItems(): any[] {
    switch (this.contextAgent()) {
      case 'designer':
        return [
          {
            label: 'Image',
            icon: 'ph ph-image',
            description: 'A design screenshot or just a sketch on a napkin',
            command: () => {
              this.imageUpload().nativeElement.click()
            },
          },
        ]
      case 'analyst':
        return [
          {
            label: 'BPMN Process',
            icon: 'ph ph-code',
            description: 'Import an existing BPMN Process',
            command: () => {
              this.processUpload().nativeElement.click()
            },
          },
          {
            label: 'Document',
            icon: 'ph ph-file-pdf',
            description: 'A business process document',
            command: () => {
              this.docUpload().nativeElement.click()
            },
          },
          {
            label: 'Image',
            icon: 'ph ph-image',
            description: 'A process screenshot',
            command: () => {
              this.imageUpload().nativeElement.click()
            },
          },
        ]
      default:
        return [
          {
            label: 'Document',
            icon: 'ph ph-file-pdf',
            description: 'A PDF document',
            command: () => {
              this.docUpload().nativeElement.click()
            },
          },
        ]
    }
  }

  // TODO: Refactor this to be a computed signal
  canSubmitPrompt(): boolean {
    const imagesSelected = !!this.#imageFiles().length
    const bpmnFilesSelected = this.#bpmnFilesSelected()
    const textValid = this.typeControl.valid
    const aiPending = this.#store.selectSignal(AiSelectors.pendingAiResponse)()

    return (imagesSelected || bpmnFilesSelected || textValid) && !aiPending
  }

  // TODO: Rename imageFiles to files everywhere (components and most probably store)
  submitTypeControl(): void {
    if (this.canSubmitPrompt()) {
      this.submitted.emit(true)
      // clear selected agent and remove it from textarea
      this.agentControl.setValue(null)
      // persist removal to store
      this.#store.dispatch(AiActions.setSelectedAgent({ selectedAgent: null }))
      this.#store.dispatch(
        AiActions.typeAreaSubmit({
          text: this.typeControl.value,
          files: [...this.#imageFiles(), ...this.#bpmnFiles()].map((file) => file.file),
          agent: this.agentControl.value
            ? AiAgent[this.agentControl.value.toUpperCase() as keyof typeof AiAgent]
            : null,
        })
      )

      this.#resetAndFocusTextarea()
    }
  }

  onFileSelected(event: Event): void {
    this.#store.dispatch(AiActions.fileUploaded({ event }))
    resetInputFileValueFromEvent(event)
  }

  typeText(text: string, delay = 5): void {
    this.typeControl.setValue('') // Clear before typing
    let i = 0
    const typeNext = (): void => {
      if (i <= text.length) {
        this.typeControl.setValue(text.slice(0, i))
        i++
        if (i <= text.length) {
          setTimeout(typeNext, delay)
        }
      }
    }
    typeNext()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.typeText(this.value())
    }
  }

  ngOnDestroy(): void {
    if (this.typingInterval) {
      clearInterval(this.typingInterval)
    }
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
    }
    clearNode(this.#effector)
  }

  animateHintTyping(hint: string, speed: number = 50, onComplete?: () => void): void {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
    }
    this.animatedHint = ''
    let i = 0
    const typeNext = (): void => {
      if (i <= hint.length) {
        this.animatedHint = hint.slice(0, i)
        i++
        this.typingTimeout = setTimeout(typeNext, speed)
      } else {
        if (onComplete) {
          onComplete()
        }
      }
    }
    typeNext()
  }

  startTypingInterval(): void {
    // Clear any existing timers to avoid duplicates
    if (this.typingInterval) {
      clearTimeout(this.typingInterval)
    }
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
    }

    const hintsArr = this.hints()?.length ? this.hints() : [this.hint()]
    if (!hintsArr || hintsArr.length === 0) {
      return
    }
    this.hintIndex = 0

    const run = (): void => {
      const current = hintsArr[this.hintIndex]
      this.animateHintTyping(current, 50, () => {
        this.hintIndex = (this.hintIndex + 1) % hintsArr.length
        this.typingInterval = setTimeout(() => run(), 5000)
      })
    }

    run()
  }

  #onCopyToChatInputField(): void {
    this.#actions$
      .pipe(ofType(AiActions.copyToChatInputField), takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ text }) => {
        this.typeControl.setValue(text)
      })
  }

  #resetAndFocusTextarea(): void {
    this.typeControl.reset()
    this.textarea()?.nativeElement.focus()
  }
}
